/**
 * Viewport helpers.
 *
 * The design prototype was drawn at desktop width and every page style is an
 * inline object, which a media query cannot reach. Two tools cover that:
 *
 *   · `fluid()` builds a `clamp()` string, which *is* legal inside an inline
 *     style. Anything that only needs to get smaller — type, padding, gaps —
 *     uses it, so no breakpoint bookkeeping is involved.
 *   · `useIsMobile()` / `useIsTouch()` for the cases where the layout genuinely
 *     changes shape rather than scale: the nav collapsing into a drawer, the
 *     account rail turning into a scroller, the overlay scrollbar going away on
 *     touch devices.
 *
 * Structural collapses that a class can express still live in styles.css.
 */

import { useEffect, useState } from 'react';

/** Matches the breakpoints in styles.css. Keep the two in step. */
export const BREAKPOINTS = { narrow: 600, mobile: 900 } as const;

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Below the point where the desktop nav and multi-column grids stop working. */
export const useIsMobile = (): boolean => useMediaQuery(`(max-width: ${BREAKPOINTS.mobile}px)`);

/** Phone-width: single column even where tablet keeps two. */
export const useIsNarrow = (): boolean => useMediaQuery(`(max-width: ${BREAKPOINTS.narrow}px)`);

/** No hover and no precise pointer — i.e. do not draw hover-only affordances. */
export const useIsTouch = (): boolean => useMediaQuery('(hover: none), (pointer: coarse)');

/**
 * A `clamp()` that interpolates linearly between two viewport widths, so a
 * 58px hero reaches its 34px floor exactly at a 360px phone rather than at some
 * arbitrary point decided by a hand-picked `vw` figure.
 */
export function fluid(min: number, max: number, fromVw = 360, toVw = 1200): string {
  const slope = (max - min) / (toVw - fromVw);
  const intercept = min - slope * fromVw;
  const vw = (slope * 100).toFixed(3);
  return `clamp(${min}px, calc(${intercept.toFixed(2)}px + ${vw}vw), ${max}px)`;
}
